/**
 * UI-11 — the screen's search box, drawn beside the screen's name.
 *
 * Every screen that filters a list owns its term: it is in that screen's query
 * key, its URL and its debounce, and moving the state into the shell would mean
 * the shell knowing what a quote, a reminder and a scanned invoice each search
 * by. So the term stays where it is and only the box moves — the screen hands
 * the bar a value, a placeholder and a way to change it, and takes it back when
 * it leaves.
 *
 * A module-level store rather than a context, for two reasons. The bar is above
 * the screens in the tree, so a provider would have to sit above both and the
 * screens would be publishing upwards through it anyway; and a store can be
 * exercised without React, which is what the tests beside this file do.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';

export interface PageSearchSlot {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** The screen is fetching what the term asked for; the box shows a spinner. */
  busy?: boolean;
}

let slot: PageSearchSlot | null = null;
const listeners = new Set<() => void>();

/**
 * `owner` is the slot the caller last published. Clearing names it, because
 * React unmounts the screen being left *after* mounting the one replacing it:
 * a cleanup that blanked the bar unconditionally would wipe the box the new
 * screen had already filled.
 */
export function setPageSearch(next: PageSearchSlot | null, owner?: PageSearchSlot): void {
  if (next === null && owner && slot !== owner) return;
  if (slot === next) return;
  /* An identical slot is not news. Without this the bar re-renders on every
     render of the screen, and the box it draws loses the caret. */
  if (
    slot &&
    next &&
    slot.value === next.value &&
    slot.placeholder === next.placeholder &&
    Boolean(slot.busy) === Boolean(next.busy) &&
    slot.onChange === next.onChange
  ) {
    return;
  }
  slot = next;
  for (const listener of listeners) listener();
}

export function getPageSearch(): PageSearchSlot | null {
  return slot;
}

export function subscribePageSearch(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * What a screen calls: "while I am here, this is what the bar searches."
 *
 * The handler is held in a ref and published through a wrapper, so a screen
 * passing an inline arrow — which is every screen — does not republish the slot
 * on every render of itself.
 */
export function usePageSearch(
  value: string,
  onChange: (value: string) => void,
  placeholder?: string,
  /* False on a screen whose tabs do not all search the same thing — the
     mini-site searches galleries and nothing else, and a box over its other
     tabs would be a box that filters nothing. */
  active = true,
  /* True while the screen fetches the results of a new term. */
  busy = false,
): void {
  const handler = useRef(onChange);
  handler.current = onChange;
  const publish = useRef<(value: string) => void>((next) => handler.current(next));

  useEffect(() => {
    if (!active) return;
    const mine: PageSearchSlot = {
      value,
      onChange: publish.current,
      ...(placeholder ? { placeholder } : {}),
      ...(busy ? { busy } : {}),
    };
    setPageSearch(mine);
    return () => setPageSearch(null, mine);
  }, [value, placeholder, active, busy]);
}

/** What the bar calls: whatever the screen on display last handed over. */
export function usePageSearchSlot(): PageSearchSlot | null {
  return useSyncExternalStore(subscribePageSearch, getPageSearch, () => null);
}
